<template>
  <div>
    <div class="w-850 max-w-full px-15 md:px-35 md:pl-25 xl:pr-0 pb-150">
      <div class="md:mt-60 mt-40">
        <div class="flex items-center justify-between md:mb-30 mb-25">
          <p class="font-bold leading-small text-35 md:text-45 text-app-color text-opacity-90">Spaces</p>
          <div class="flex text-app-color">
            <i
              class="icon-reorder mr-5 text-23 opacity-15 cursor-pointer"
              :class="[
                {'opacity-100': settings.bookView === 'List'},
                { 'text-main-color': settings.bookView === 'List' && (isPoseidonTheme) }
              ]"
              @click="updateBookView('List')"
            />
            <i
              class="icon-view_stream mr-5 text-25 opacity-15 cursor-pointer"
              :class="[
                {'opacity-100': settings.bookView === 'Block'},
                { 'text-main-color': settings.bookView === 'Block' && (isPoseidonTheme) }
              ]"
              @click="updateBookView('Block')"
            />
            <i
              class="icon-dashboard text-25 opacity-15 cursor-pointer"
              :class="[
                {'opacity-100': settings.bookView === 'Card'},
                { 'text-main-color': settings.bookView === 'Card' && (isPoseidonTheme) }
              ]"
              @click="updateBookView('Card')"
            />
          </div>
        </div>

        <Headquarters v-if="isTeamAccount" />

        <div v-if="hasBooks" id="books-container" class="my-15 md:my-30">
          <draggable
            v-model="orderedBooks"
            :delay="300"
            :delay-on-touch-only="true"
            :force-fallback="true"
            item-key="id"
            filter=".no-drag"
            :class="[
              {'grid grid-cols-2 lg:grid-cols-3 gap-10 md:gap-18': settings.bookView === 'Card'},
              {'grid grid-cols-1 gap-10 md:gap-18': settings.bookView === 'Block'}
            ]"
            :disabled="isDragDisabled"
            @end="onDragEnd"
            @start="onStart"
          >
            <template #item="{ element, index }">
              <component
                :is="element.isSection ? 'Section': cardType "
                :ref="el => bookRefs[index] = el"
                v-model="isDragDisabled"
                :book="element"
                :index="index"
                :is-last-item="index === orderedBooks.length - 1"
                class="select-none"
                :class="[
                  { 'col-span-full': element.isSection },
                  element.hasPadding && element.isSection ? index !== 0 ? 'pt-40' : 'pt-20' : ''
                ]"
                @delete="showConfirm(element)"
              />
            </template>
          </draggable>
        </div>
        <div v-else class="mb-20">No spaces</div>
        <div>
          <ButtonModule
            text="new space"
            class="text-15 rounded-4 px-14 py-11 md:py-14 md:px-17 text-white mr-15 hover:opacity-80 bg-link-color"
            prefix="icon-add text-11 mt-2"
            button-opacity="100"
            @click="addBook"
          />
          <ButtonModule
            text="add section"
            class="text-15 text-grey-900 opacity-30 hover:opacity-100 anim-opacity"
            prefix="icon-add text-11 mt-2"
            @click="addSection"
          />
        </div>
      </div>
    </div>

    <div v-if="deletedBooks.length">
      <transition-group name="fade">
        <DeleteNotification
          v-for="(item, index) in deletedBooks"
          :key="item.id"
          :index="index"
          type="Space"
          @close="closeNotification(item.id)"
          @restore="restore(item.id)"
        />
      </transition-group>
    </div>
    <ConfirmModal
      ref="confirm"
      title="Delete space"
      confirm-btn-title="Delete"
      confirm-btn-class="bg-error"
      @confirm="deleteBook"
    >
      <p class="text-14 mt-20 mb-25">Are you sure you want to delete a space with all the pages in it?</p>
    </ConfirmModal>

    <Badge v-if="isOwner && isTrialPlan && isTeamAccount" />
    <TrialIsOver v-if="isTeamAccount" />
  </div>
</template>

<script>
import Headquarters from '@/components/books/Headquarters'
import Block from '@/components/books/Block'
import Card from '@/components/books/Card'
import List from '@/components/books/List'
import Section from '@/components/books/Section'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import DeleteNotification from '@/components/shared/DeleteNotification'
import ConfirmModal from '@/components/confirm/ConfirmModal'
import Badge from '@/components/team/Badge'
import TrialIsOver from '@/components/team/TrialIsOver'

import draggable from 'vuedraggable'
import draggableMixin from '@/mixins/draggable'

import { bookService, settingsService } from '@/services'
import localStorageService from '@/core/local-storage'

import { defineComponent, ref, computed, onBeforeUpdate } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  components: {
    Headquarters,
    Block,
    Card,
    List,
    Section,
    ButtonModule,
    DeleteNotification,
    ConfirmModal,
    draggable,
    Badge,
    TrialIsOver
  },

  mixins: [draggableMixin],

  setup () {
    const { state, getters, commit, dispatch } = useStore()

    const book = ref(null)
    const confirm = ref(null)
    const deletedBooks = ref([])
    const isDragDisabled = ref(false)
    const bookRefs = ref([])

    const books = computed(() => state.book.books)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isTrialPlan = computed(() => getters.isTrialPlan)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isOwner = computed(() => getters.isOwner)
    const hasBooks = computed(() => getters.hasBooks)
    const settings = computed(() => state.settings.settings)
    const cardType = computed(() => settings.value.bookView || 'Card')
    const isPoseidonTheme = computed(() => settings.value.theme === 'Poseidon')
    const isMagenta = computed(() => getters.isMagenta)
    const isDraculaTheme = computed(() => settings.value.theme === 'Dracula')

    const orderedBooks = computed({
      get: () => {
        const arr = []
        books.value.reduce((accumulator, currentValue) => {
          if (accumulator.isSection && currentValue.isSection) {
            currentValue.hasPadding = false
          } else {
            currentValue.hasPadding = true
          }

          arr.push(currentValue)
          return currentValue
        }, 10)

        return books.value
      },
      set: (value) => {
        commit('setBooks', value)
        const ids = value.map(item => item.id)
        bookService.order(ids)
      }
    })

    onBeforeUpdate(() => {
      bookRefs.value = []
    })

    dispatch('getBooks')
    commit('clearBook')

    function addBook () {
      dispatch('addBook').then((res) => {
        setTimeout(() => {
          const index = bookRefs.value.findIndex(item => item.book.id === res.id)
          if (typeof bookRefs.value[index].toggleFullEdit() === 'function') {
            bookRefs.value[index].toggleFullEdit()
          }
        }, 500)
      })
    }

    function addSection () {
      dispatch('addSection').then((res) => {
        setTimeout(() => {
          const index = bookRefs.value.findIndex(item => item.book.id === res.id)
          if (typeof bookRefs.value[index].setFocus() === 'function') {
            bookRefs.value[index].setFocus()
          }
        }, 500)
      })
    }

    function clearStorage () {
      localStorageService.removeItem('lastOpenedProject')
    }

    function restore (id) {
      dispatch('restoreBook', id)
    }

    function showConfirm (item) {
      book.value = item
      confirm.value.open()
    }

    function deleteBook () {
      dispatch('deleteBook', { id: book.value.id, isSection: false }).then(_ => {
        confirm.value.close()
        deletedBooks.value.push(book.value)
      })
    }

    function closeNotification (id) {
      deletedBooks.value = deletedBooks.value.filter(item => item.id !== id)
    }

    function updateBookView (value) {
      const design = {
        ...settings.value,
        bookView: value
      }

      commit('updateSettings', { id: settings.value.id, ...design })
      settingsService.update(design)
    }

    return {
      bookRefs,
      // data
      deletedBooks,
      book,
      confirm,
      isDragDisabled,
      // computeds
      books,
      settings,
      isMobile,
      isDarkMode,
      isTrialPlan,
      isOwner,
      isTeamAccount,
      cardType,
      orderedBooks,
      isPoseidonTheme,
      isDraculaTheme,
      isMagenta,
      hasBooks,
      // functions
      addBook,
      addSection,
      clearStorage,
      restore,
      showConfirm,
      deleteBook,
      closeNotification,
      updateBookView
    }
  },

  mounted () {
    window.addEventListener('beforeunload', this.clearStorage)
  },

  unmounted () {
    window.removeEventListener('beforeunload', this.clearStorage)
  }
})
</script>
